import type { Question, QuestionAnswer } from "./types";
import type { Translate } from "./i18n";

interface Handlers {
  questions: Question[];
  onAnswers: (answers: QuestionAnswer[]) => void;
  studyName?: string;
  /** Floating "Befragung abschließen"-Button anzeigen (Selbst-Abschluss, Observe). */
  withFinishButton?: boolean;
  /** Übersetzer (probanden-sichtbare Texte). */
  t: Translate;
}

export interface QuestionsHandle {
  /** Vorab-Modal (pre_task) zeigen — idempotent (Guard gegen Reconnect-Config). */
  showPre: () => void;
  /** Abschluss-Modal (post_task + post_session) zeigen — z. B. wenn der Moderator beendet. */
  showEnd: () => void;
  cleanup: () => void;
}

const STYLE = `
:host { all: initial; }
* { box-sizing: border-box; font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; }
.backdrop {
  position: fixed; inset: 0; z-index: 2147483600; background: rgba(0,0,0,.45);
  display: flex; align-items: center; justify-content: center; padding: 20px;
}
.modal {
  background: #fff; color: #18181b; width: min(520px, 100%); max-height: 85vh; overflow: auto;
  border-radius: 14px; padding: 22px; box-shadow: 0 12px 40px rgba(0,0,0,.3);
}
.modal h2 { margin: 0 0 4px; font-size: 18px; }
.modal .sub { color: #71717a; font-size: 13px; margin: 0 0 16px; }
.q { margin-bottom: 18px; }
.q .prompt { font-size: 14px; font-weight: 600; margin-bottom: 8px; }
.scale { display: flex; flex-wrap: wrap; gap: 6px; }
.scale button { min-width: 38px; padding: 7px 10px; border: 1px solid #e4e4e7; border-radius: 8px; background: #fff; cursor: pointer; font-size: 14px; }
.scale button.sel { background: #2563eb; border-color: #2563eb; color: #fff; }
.opt { display: flex; align-items: center; gap: 8px; padding: 6px 0; cursor: pointer; font-size: 14px; }
.opt input { width: auto; }
textarea { width: 100%; min-height: 64px; border: 1px solid #e4e4e7; border-radius: 8px; padding: 8px; font: inherit; resize: vertical; }
.actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px; }
.btn { padding: 8px 16px; border-radius: 8px; border: 1px solid #e4e4e7; background: #fff; cursor: pointer; font-size: 14px; }
.btn.primary { background: #2563eb; border-color: #2563eb; color: #fff; font-weight: 600; }
.finish {
  position: fixed; right: 16px; bottom: 16px; z-index: 2147483500; border: none; cursor: pointer;
  padding: 10px 16px; border-radius: 999px; background: #16a34a; color: #fff; font-size: 14px; font-weight: 600;
  box-shadow: 0 4px 14px rgba(0,0,0,.25);
}
.thanks {
  position: fixed; right: 16px; bottom: 16px; z-index: 2147483500; background: #16a34a; color: #fff;
  padding: 10px 16px; border-radius: 999px; font-size: 14px; box-shadow: 0 4px 14px rgba(0,0,0,.25);
}
`;

/**
 * Zeigt dem Probanden die Studien-Fragen als Modal (nur Beobachtungs-Modus):
 * pre_task vorab über Backdrop, post_task/post_session als Abschluss-Befragung
 * über einen „Befragung abschließen“-Button. Isoliertes Shadow-DOM, Host mit
 * `rr-block`, damit die Befragung nicht ins rrweb-Replay gerät.
 */
export function attachQuestions({ questions, onAnswers, studyName, withFinishButton, t }: Handlers): QuestionsHandle {
  const pre = questions.filter((q) => q.scope === "pre_task");
  // Alles Nicht-Pre kommt ans Ende (post_task, post_session, ohne Scope).
  const end = questions.filter((q) => q.scope !== "pre_task");

  const host = document.createElement("div");
  host.className = "rr-block rr-ignore";
  host.setAttribute("data-ul-questions", "");
  document.body.appendChild(host);
  const root = host.attachShadow({ mode: "open" });
  const style = document.createElement("style");
  style.textContent = STYLE;
  root.appendChild(style);

  let preShown = false;
  let endDone = false;
  let endOpen = false;
  let finishBtn: HTMLButtonElement | null = null;

  function buildQuestion(q: Question): { el: HTMLElement; read: () => QuestionAnswer | null } {
    const wrap = document.createElement("div");
    wrap.className = "q";
    const prompt = document.createElement("div");
    prompt.className = "prompt";
    prompt.textContent = q.prompt || "";
    wrap.appendChild(prompt);

    if (q.type === "scale") {
      const min = q.scale?.min ?? 1;
      const max = q.scale?.max ?? 7;
      const row = document.createElement("div");
      row.className = "scale";
      let sel: number | null = null;
      for (let v = min; v <= max; v++) {
        const b = document.createElement("button");
        b.type = "button";
        b.textContent = String(v);
        const val = v;
        b.addEventListener("click", () => {
          sel = val;
          row.querySelectorAll("button").forEach((x) => x.classList.remove("sel"));
          b.classList.add("sel");
        });
        row.appendChild(b);
      }
      wrap.appendChild(row);
      return { el: wrap, read: () => (sel == null ? null : { questionId: q.id, value: sel }) };
    }

    if (q.type === "single_choice") {
      const name = "q_" + q.id;
      let sel: string | null = null;
      for (const opt of q.options || []) {
        const lab = document.createElement("label");
        lab.className = "opt";
        const r = document.createElement("input");
        r.type = "radio";
        r.name = name;
        r.value = opt;
        r.addEventListener("change", () => {
          sel = opt;
        });
        lab.appendChild(r);
        lab.appendChild(document.createTextNode(opt));
        wrap.appendChild(lab);
      }
      return { el: wrap, read: () => (sel == null ? null : { questionId: q.id, value: sel }) };
    }

    // text (Default)
    const ta = document.createElement("textarea");
    wrap.appendChild(ta);
    return {
      el: wrap,
      read: () => {
        const v = ta.value.trim();
        return v ? { questionId: q.id, value: v } : null;
      },
    };
  }

  function openModal(
    title: string,
    qs: Question[],
    submitLabel: string,
    onSubmit: (answers: QuestionAnswer[]) => void
  ): void {
    const backdrop = document.createElement("div");
    backdrop.className = "backdrop";
    const modal = document.createElement("div");
    modal.className = "modal";

    const h = document.createElement("h2");
    h.textContent = title;
    modal.appendChild(h);
    if (studyName) {
      const s = document.createElement("p");
      s.className = "sub";
      s.textContent = studyName;
      modal.appendChild(s);
    }

    const built = qs.map(buildQuestion);
    built.forEach((b) => modal.appendChild(b.el));

    const actions = document.createElement("div");
    actions.className = "actions";
    const submit = document.createElement("button");
    submit.className = "btn primary";
    submit.textContent = submitLabel;
    submit.addEventListener("click", () => {
      const answers = built.map((b) => b.read()).filter((a): a is QuestionAnswer => a !== null);
      onSubmit(answers);
      backdrop.remove();
    });
    actions.appendChild(submit);
    modal.appendChild(actions);

    backdrop.appendChild(modal);
    root.appendChild(backdrop);
  }

  // Abschluss-Modal (post_task + post_session). Idempotent: nicht doppelt öffnen,
  // nicht nach Abschluss. Auslöser: Selbst-Button (Observe) ODER "ended" vom Moderator.
  function openEndModal(): void {
    if (endDone || endOpen || end.length === 0) return;
    endOpen = true;
    openModal(t("questions.endTitle"), end, t("questions.submit"), (answers) => {
      if (answers.length) onAnswers(answers);
      endDone = true;
      endOpen = false;
      finishBtn?.remove();
      finishBtn = null;
      const thanks = document.createElement("div");
      thanks.className = "thanks";
      thanks.textContent = t("questions.thanks");
      root.appendChild(thanks);
      setTimeout(() => thanks.remove(), 4000);
    });
  }

  function renderFinishButton(): void {
    if (!withFinishButton || finishBtn || endDone || end.length === 0) return;
    const btn = document.createElement("button");
    btn.className = "finish";
    btn.textContent = t("questions.finish");
    btn.addEventListener("click", openEndModal);
    finishBtn = btn;
    root.appendChild(btn);
  }

  function showPre(): void {
    if (preShown) return;
    preShown = true;
    if (pre.length) {
      openModal(t("questions.preTitle"), pre, t("questions.next"), (answers) => {
        if (answers.length) onAnswers(answers);
        renderFinishButton();
      });
    } else {
      renderFinishButton();
    }
  }

  // Verlassen-Warnung, solange Abschlussfragen noch offen sind.
  const onBeforeUnload = (e: BeforeUnloadEvent) => {
    if (end.length > 0 && !endDone) {
      e.preventDefault();
      e.returnValue = "";
    }
  };
  window.addEventListener("beforeunload", onBeforeUnload);

  return {
    showPre,
    showEnd: openEndModal,
    cleanup: () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      host.remove();
    },
  };
}
